import { tv } from "tailwind-variants";
import { useTimelineProgress } from "@/state/selectors";
import DefaultContentContainer from "@/components/shared/DefaultContainer/DefaultContentContainer";
import { CSSProperties, useEffect, useRef, useState } from "react";
import ClockIcon from "@/components/icons/ClockIcon";

const START_YEAR = 2026;
const END_YEAR = 2046;
const CHIP_WIDTH = "32px";
const CHIP_GAP = "4px";
const THUMB_WIDTH = 44;

const timeline = tv({
    slots: {
        outer: "relative w-full h-8",
        chipBase: "absolute top-1/2 -translate-y-1/2 z-10 overflow-hidden w-[32px] text-center",
        chipLeft: "left-0",
        chipRight: "right-0",
        track: "absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-black/60 overflow-hidden",
        filled: "absolute inset-0 bg-white origin-left rounded-full",
        thumb: "absolute left-0 top-1/2 z-20 w-[44px] h-[24px] text-center rounded-full bg-white text-xs font-bold text-black will-change-transform flex items-center justify-center",
    },
});

export function ProgressSlider() {
    const { overallProgress } = useTimelineProgress();
    const { outer, chipBase, chipLeft, chipRight, track, filled, thumb } = timeline();
    const clampedProgress = Math.min(1, Math.max(0, overallProgress));
    const currentYear = Math.round(START_YEAR + clampedProgress * (END_YEAR - START_YEAR));

    const outerRef = useRef<HTMLDivElement>(null);
    const [trackWidth, setTrackWidth] = useState(0);

    useEffect(() => {
        if (!outerRef.current) return;
        const obs = new ResizeObserver(([e]) => setTrackWidth(e.contentRect.width));
        obs.observe(outerRef.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div ref={outerRef} className={outer()} style={{ "--chip-w": CHIP_WIDTH } as CSSProperties}>
            {/* Year chips */}
            <div className={`${chipBase()} ${chipLeft()}`}>
                <DefaultContentContainer>
                    <div className="text-[8px] py-1 font-bold">{START_YEAR}</div>
                </DefaultContentContainer>
            </div>
            <div className={`${chipBase()} ${chipRight()}`}>
                <DefaultContentContainer>
                    <div className="text-[8px] py-1 font-bold">{END_YEAR}</div>
                </DefaultContentContainer>
            </div>

            {/* Track */}
            <div
                className={track()}
                style={{
                    left: `calc(var(--chip-w) + ${CHIP_GAP})`,
                    right: `calc(var(--chip-w) + ${CHIP_GAP})`,
                }}
            >
                {/* Fill */}
                <div
                    className={filled()}
                    style={{
                        transform: `scaleX(${clampedProgress})`,
                    }}
                />
            </div>

            {/* Thumb — positioned via transform */}
            <div
                className={thumb()}
                style={{
                    transform: `translateY(-50%) translateX(${clampedProgress * (trackWidth - THUMB_WIDTH)}px)`,
                }}
            >
                <div className="absolute -top-4 flex items-center w-full justify-center gap-x-[2.5px] text-white text-[12px]">
                    <span>Jahr</span>
                    <ClockIcon width={10} height={10} />
                </div>
                {currentYear}
            </div>
        </div>
    );
}